// hooks/useLoadingContent.js
import { useReducer, useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { listDays } from "../actions/dayActions";
import quoteObj from "../db/Quotes";
import { memorySelector } from "../utils/UtilityFunctions";
import {
  readLoadingPrefs,
  hasSeenBacklogTile,
  markBacklogTileSeen,
} from "../utils/loadingPreferences";
import { countDaysNeedingScore } from "../utils/dayProgress";

const quoteContent = (quote) => ({
  type: "quote",
  text: `"${quote.quote}"`,
  author: quote.author,
  authorLink: quote.authorLink,
  otherLink: quote.otherLink,
});

/* Days written into but never given a feeling score — work the user can
   actually go and finish.

   `author` carries the instruction line because the welcome and milestone
   tiles already use that field for their second line and the reducer copies
   it; no new state key is needed. The count is the one number allowed on a
   tile: it is a plain count of things you can go and fix, not a meter, a
   percentage or a streak. */
const backlogContent = (count) => ({
  type: "backlog",
  text:
    count === 1
      ? "One day is waiting for a feeling score."
      : `${count} days are waiting for a feeling score.`,
  author: "Open your journal log, filtered to the days that need one.",
});

/* Nothing to read. The tile falls back to a bare loader and, because it is
   marked suppressed, the screen drops its loading floor too — so the page
   arrives as soon as its data does. See utils/loadingPreferences.js. */
const suppressedContent = () => ({
  type: "loading",
  text: "Loading...",
  author: "",
  suppressed: true,
});

/* Pure: the coin flip is passed in rather than rolled here, so the same inputs
   always give the same tile. Rolling it inside meant every re-render that
   changed `days` produced different content while the user was reading. */
const getDisplayContent = (days, quote, memory, coinFlip, prefs, backlogSeen) => {
  const totalDays = days.length;

  if (totalDays === 0) {
    return {
      type: "welcome",
      text: "Welcome to your NoWastedDays!",
      author: "Start your first day by clicking on the journal icon.",
    };
  }
  if (totalDays === 10) {
    return {
      type: "milestone",
      text: "Your insights are live! Go check them out.",
      author: "",
      celebration: true,
    };
  }
  if (totalDays > 10 && totalDays % 7 === 0) {
    return {
      type: "milestone",
      text: "You've unlocked another week of insight data.",
      author: "Check out your graphs",
      celebration: true,
    };
  }
  /* Above memories and quotes because it is work rather than filler; below a
     milestone because a celebration should not be interrupted. Once per
     session, then out of the way — see utils/loadingPreferences.js. */
  if (!backlogSeen) {
    const needing = countDaysNeedingScore(days);
    if (needing > 0) return backlogContent(needing);
  }

  if (totalDays >= 1) {
    /* Either source can be switched off from the tile itself. A memory also
       has to exist — it only does once a day has been journalled with
       something in "remember today". */
    const memoryContent = {
      type: "memory",
      text: memory?.memory,
      author: memory?.date,
    };
    const canShowMemory = prefs.memories && Boolean(memory && memory.memory);
    const canShowQuote = prefs.quotes;

    if (canShowMemory && canShowQuote) {
      return coinFlip > 0.5 ? memoryContent : quoteContent(quote);
    }
    /* With only one source left the coin flip goes: someone who turned quotes
       off should see their memory every time, not half the time. */
    if (canShowMemory) return memoryContent;
    if (canShowQuote) return quoteContent(quote);

    /* Both ruled out. No filler and no silent substitution. */
    return suppressedContent();
  }
  return suppressedContent();
};

const initialState = {
  tileType: "loading",
  text: "Loading...",
  author: "",
  celebration: false,
  suppressed: false,
};

function tileReducer(state, action) {
  switch (action.type) {
    case "SET_CONTENT":
      return {
        ...state,
        tileType: action.payload.type,
        text: action.payload.text,
        author: action.payload.author,
        celebration: action.payload.celebration || false,
        suppressed: action.payload.suppressed || false,
        authorLink: action.payload.authorLink
          ? action.payload.authorLink
          : false,
        otherLink: action.payload.otherLink ? action.payload.otherLink : false,
      };
    default:
      return state;
  }
}

export const useLoadingContent = () => {
  const dispatch = useDispatch();
  const [state, dispatchState] = useReducer(tileReducer, initialState);

  /* One quote and one coin flip for the lifetime of this mount. */
  const [selectedQuote] = useState(
    () => quoteObj[Math.floor(Math.random() * quoteObj.length)],
  );
  const [coinFlip] = useState(() => Math.random());

  /* Read once per mount, for the same reason as the quote and the coin flip
     above: a switch flipped mid-read must not swap the tile out from under
     whoever is reading it. The live hook is for the settings page. */
  const prefs = useRef(readLoadingPrefs());

  /* Read once per mount, same reasoning. Captured up front so nothing can
     re-read storage mid-decision and swap the tile out. */
  const backlogSeen = useRef(hasSeenBacklogTile());

  const { userInfo } = useSelector((state) => state.userLogin);
  const { days, loading } = useSelector((state) => state.dayList);

  useEffect(() => {
    dispatch(listDays());
  }, [dispatch, userInfo]);

  /* The tile shows ONE thing per visit. It is decided once, when the day list
     has actually resolved, and never revisited — the user is reading it, and
     swapping a memory out from under them mid-read is the bug this guards.

     Waiting for `loading === false` matters: dayListReducer starts at
     { days: [] } and drops `days` entirely during the request, so deciding on
     the first pass would show an existing user the first-time "Welcome" tile
     and then replace it a moment later. */
  const decided = useRef(false);

  useEffect(() => {
    if (decided.current || loading !== false) return;

    decided.current = true;

    // A failed fetch leaves no day list. Fall back to a quote rather than an
    // empty list, which would read as "welcome, you have no days" — unless
    // quotes are switched off, in which case a failure is no excuse to show
    // one.
    const dayList = Array.isArray(days) ? days : null;
    const fallback = prefs.current.quotes
      ? quoteContent(selectedQuote)
      : suppressedContent();
    const content = dayList
      ? getDisplayContent(
          dayList,
          selectedQuote,
          memorySelector(dayList),
          coinFlip,
          prefs.current,
          backlogSeen.current,
        )
      : fallback;

    /* Burned on decision, not display. `decided` already guarantees one
       decision per mount, so this writes at most once, in the same tick as
       the content that earned it. */
    if (content.type === "backlog") markBacklogTileSeen();

    dispatchState({ type: "SET_CONTENT", payload: content });
  }, [loading, days, selectedQuote, coinFlip]);

  return state; // Expose the content state to the component
};
